import React, { useEffect, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FaCheck } from "react-icons/fa";
import { t } from "../i18n/translations";
import { trackOrder } from "../actions/orderActions";
import { ORDER_STATUSES, statusIndex } from "../constants/orderStatus";
import "./OrderSuccessScreen.css";

/* One-shot confetti burst radiating from the checkmark. Deterministic
   offsets (no randomness) so the moment plays identically every time;
   colors stay on the restaurant's accent tokens. */
const BURST_PARTICLES = [
  { tx: -108, ty: -42, rot: -140, size: 11, tone: "accent" },
  { tx: -78, ty: -98, rot: 120, size: 9, tone: "accent-active" },
  { tx: -22, ty: -120, rot: -60, size: 10, tone: "accent" },
  { tx: 42, ty: -110, rot: 90, size: 8, tone: "neutral" },
  { tx: 98, ty: -75, rot: -110, size: 11, tone: "accent-hover" },
  { tx: 120, ty: -12, rot: 70, size: 9, tone: "accent" },
  { tx: 105, ty: 55, rot: -80, size: 10, tone: "accent-active" },
  { tx: 55, ty: 105, rot: 130, size: 8, tone: "accent" },
  { tx: -10, ty: 122, rot: -50, size: 9, tone: "neutral" },
  { tx: -72, ty: 100, rot: 100, size: 11, tone: "accent-hover" },
  { tx: -118, ty: 35, rot: -120, size: 8, tone: "accent" },
  { tx: -50, ty: -25, rot: 60, size: 7, tone: "accent-active" },
];

function OrderSuccessScreen() {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const orderNumber = query.get("number");
  const orderId = query.get("id");
  const orderToken = query.get("token");

  const dispatch = useDispatch();
  const { language } = useSelector((state) => state.language);
  const { order } = useSelector((state) => state.orderTrack);

  const pollRef = useRef(null);

  /* ===== POLL ORDER STATUS — every 5s (same interval used by the
     admin Orders screen), stops once the order is completed. ===== */
  useEffect(() => {
    if (!orderId || !orderToken) return;

    dispatch(trackOrder(orderId, orderToken));

    pollRef.current = setInterval(() => {
      dispatch(trackOrder(orderId, orderToken));
    }, 5000);

    return () => clearInterval(pollRef.current);
  }, [dispatch, orderId, orderToken]);

  useEffect(() => {
    if (order?.status === "completed" && pollRef.current) {
      clearInterval(pollRef.current);
    }
  }, [order?.status]);

  const currentIndex = order ? statusIndex(order.status) : -1;
  const isDone = order?.status === "completed";

  return (
    <div className="success-animation">
      <div className="success-hero">
        <span className="success-ring" aria-hidden="true" />

        <div className="success-burst" aria-hidden="true">
          {BURST_PARTICLES.map((p, i) => (
            <span
              key={i}
              className={`success-burst-piece tone-${p.tone}`}
              style={{
                "--tx": `${p.tx}px`,
                "--ty": `${p.ty}px`,
                "--rot": `${p.rot}deg`,
                width: `${p.size}px`,
                height: `${p.size}px`,
              }}
            />
          ))}
        </div>

        <svg
          className="checkmark"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 52 52"
        >
          <circle
            className="checkmark__circle"
            cx="26"
            cy="26"
            r="25"
            fill="none"
          />
          <path
            className="checkmark__check"
            fill="none"
            d="M14.1 27.2l7.1 7.2 16.7-16.8"
          />
        </svg>
      </div>

      <h2 className="success-title">{t("orderSuccessTitle", language)}</h2>

      {orderNumber && (
        <p className="success-order-pill">
          <span className="success-order-label">
            {t("orderNumberLabel", language)}
          </span>
          <span className="success-order-number">{orderNumber}</span>
        </p>
      )}

      {orderId && orderToken && (
        <div className="order-tracking">
          <h3 className="order-tracking-title">
            {t("trackingTitle", language)}
          </h3>
          <p className="order-tracking-subtitle">
            <span
              className={`tracking-live-dot${isDone ? " done" : ""}`}
              aria-hidden="true"
            />
            {t("trackingSubtitle", language)}
          </p>

          <div className="tracking-stepper">
            {ORDER_STATUSES.map((s, idx) => {
              const reached = idx <= currentIndex;
              // The final stage is a resting "done" state, not an
              // in-progress one — only pulse steps still being worked
              // through, so a completed order settles into calm checkmarks
              // instead of pulsing forever on the last node.
              const isCurrent = idx === currentIndex && s.key !== "completed";

              return (
                <React.Fragment key={s.key}>
                  <div
                    className={`tracking-step ${reached ? "reached" : ""} ${
                      isCurrent ? "current" : ""
                    }`}
                  >
                    <div className="tracking-step-dot">
                      {reached && !isCurrent ? <FaCheck /> : s.emoji}
                    </div>
                    <div className="tracking-step-label">
                      {t(s.labelKey, language)}
                    </div>
                  </div>

                  {idx < ORDER_STATUSES.length - 1 && (
                    <div
                      className={`tracking-step-connector ${
                        idx < currentIndex ? "filled" : ""
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className="success-back">
        <Link to="/" className="success-back-btn">
          {t("backToMenu", language)}
        </Link>
      </div>
    </div>
  );
}

export default OrderSuccessScreen;
